/*
 * What the shell may show while a read it depends on is failing (frontend performance plan, W3,
 * F04). A background refresh that fails no longer replaces a usable page with an error:
 * - a first load that failed has nothing to show: it says so, with its reference and Try again;
 * - a refresh that failed for a transient reason keeps what was shown, marked as not refreshed;
 * - a session that ended (401) or a refusal (403) keeps nothing: what was cached was its
 *   authority's, and that authority is gone.
 */
import { ApiError, failureReferenceOf } from "../http.ts";

export type ReadState =
  | { readonly kind: "loading" }
  | { readonly kind: "failed"; readonly reference: string | null }
  | { readonly kind: "signed_out" }
  | { readonly kind: "refused" }
  | { readonly kind: "ready"; readonly stale: { readonly reference: string | null } | null };

/** How a read stands, from its last answer and its last failure. */
export function readState(query: { readonly data: unknown; readonly error: unknown }): ReadState {
  const { data, error } = query;
  if (error instanceof ApiError && error.signedOut) return { kind: "signed_out" };
  if (error instanceof ApiError && error.refused) return { kind: "refused" };
  if (data !== undefined)
    return { kind: "ready", stale: error ? { reference: failureReferenceOf(error) } : null };
  if (error) return { kind: "failed", reference: failureReferenceOf(error) };
  return { kind: "loading" };
}

export type SessionState =
  | { readonly kind: "loading" }
  | { readonly kind: "unreachable"; readonly reference: string | null }
  | { readonly kind: "signed_out" }
  | { readonly kind: "signed_in"; readonly stale: boolean };

/**
 * The session lookup answers `null` for a person who is signed out; a lookup that fails is not that
 * answer. Asking someone to sign in because the server could not be reached would sign them out of
 * a session they still have.
 */
export function sessionState(query: {
  readonly data: unknown;
  readonly error: unknown;
}): SessionState {
  const { data, error } = query;
  if (data === null) return { kind: "signed_out" };
  if (data !== undefined) return { kind: "signed_in", stale: error !== null };
  if (error) return { kind: "unreachable", reference: failureReferenceOf(error) };
  return { kind: "loading" };
}
